import { useEffect, useMemo } from "react";
import type { Actor, Bucket, CollectionSummary, Item } from "../api/types";
import { byPosition } from "../lib/kanban";
import { loadBuckets, store, type AppState } from "./app";
import { useStoreSelector } from "./createStore";

export function useApp<T>(selector: (s: AppState) => T): T {
  return useStoreSelector(store, selector);
}

export { byPosition };

/** Items of a collection (top-level and children), ordered by position. */
export function useCollectionItems(collectionId: string | undefined): Item[] {
  const items = useApp((s) => s.items);
  return useMemo(
    () =>
      collectionId
        ? Object.values(items)
            .filter((i) => i.collection_id === collectionId)
            .sort(byPosition)
        : [],
    [items, collectionId],
  );
}

export function useItem(id: string | undefined): Item | undefined {
  return useApp((s) => (id ? s.items[id] : undefined));
}

export function useActors(): Actor[] {
  const members = useApp((s) => s.members);
  return useMemo(() => (members ?? []).map((m) => m.actor), [members]);
}

export function useActorMap(): Map<string, Actor> {
  const actors = useActors();
  return useMemo(() => new Map(actors.map((a) => [a.id, a])), [actors]);
}

export function useAllCollections(): CollectionSummary[] {
  const cols = useApp((s) => s.collections);
  return useMemo(() => Object.values(cols).flat(), [cols]);
}

export function useMyActorId(): string | null {
  return useApp((s) => s.me?.orgs.find((o) => o.org.id === s.orgId)?.actor.id ?? null);
}

const NO_BUCKETS: Bucket[] = [];

/** Buckets of a collection (ordered); loads them once when `enabled`. */
export function useBuckets(collectionId: string | undefined, enabled: boolean): Bucket[] {
  const buckets = useApp((s) => (collectionId ? s.buckets[collectionId] : undefined));
  useEffect(() => {
    if (collectionId && enabled && !buckets) void loadBuckets(collectionId).catch(() => undefined);
  }, [collectionId, enabled, buckets]);
  return enabled ? (buckets ?? NO_BUCKETS) : NO_BUCKETS;
}
